/**
 * Утилиты для централизованной обработки ошибок
 * Устраняет дублирование throw new Error во всем проекте
 */

export class PsycoError extends Error {
  constructor(
    message: string,
    public code: string,
    public context?: Record<string, any>
  ) {
    super(message);
    this.name = 'PsycoError';
  }
}

export class ValidationError extends PsycoError {
  constructor(message: string, context?: Record<string, any>) {
    super(message, 'VALIDATION_ERROR', context);
    this.name = 'ValidationError';
  }
}

export class CalculationError extends PsycoError {
  constructor(message: string, context?: Record<string, any>) {
    super(message, 'CALCULATION_ERROR', context);
    this.name = 'CalculationError';
  }
}

export class DataError extends PsycoError {
  constructor(message: string, context?: Record<string, any>) {
    super(message, 'DATA_ERROR', context);
    this.name = 'DataError';
  }
}

/**
 * Предопределенные ошибки для часто используемых случаев
 */
export const Errors = {
  // Ошибки инициализации данных
  QUESTIONS_NOT_INITIALIZED: (context?: Record<string, any>) => 
    new DataError('Question data could not be initialized.', context),
  
  PSYCHOMETRICS_NOT_FOUND: (context?: Record<string, any>) => 
    new DataError('Psychometrics data not found.', context),
  
  // Ошибки валидации
  INVALID_ANSWERS: (context?: Record<string, any>) => 
    new ValidationError('Invalid answers format.', context),
  
  INVALID_SCORES: (context?: Record<string, any>) => 
    new ValidationError('Invalid scores format.', context),
  
  // Ошибки расчетов
  NO_VALID_SCORES: (context?: Record<string, any>) => 
    new CalculationError('No valid scores calculated.', context),
  
  CALCULATION_FAILED: (context?: Record<string, any>) => 
    new CalculationError('Calculation failed.', context),
  
  // Ошибки нормализации
  NORMALIZATION_FAILED: (context?: Record<string, any>) => 
    new DataError('Data normalization failed.', context),
  
  // Ошибки Firestore
  FIRESTORE_ERROR: (message: string, context?: Record<string, any>) => 
    new DataError(`Firestore error: ${message}`, context),
  
  DOCUMENT_NOT_FOUND: (id: string, context?: Record<string, any>) => 
    new DataError(`Document not found: ${id}`, context),
};

/**
 * Безопасное выполнение функции с обработкой ошибок
 */
export function safeExecute<T>(
  fn: () => T,
  errorMessage: string,
  context?: Record<string, any>
): T {
  try {
    return fn();
  } catch (error) {
    if (error instanceof PsycoError) {
      throw error;
    }
    throw new PsycoError(errorMessage, 'EXECUTION_ERROR', {
      ...context,
      originalError: error instanceof Error ? error.message : String(error)
    });
  }
}

/**
 * Безопасное выполнение асинхронной функции с обработкой ошибок
 */
export async function safeExecuteAsync<T>(
  fn: () => Promise<T>,
  errorMessage: string,
  context?: Record<string, any>
): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    if (error instanceof PsycoError) {
      throw error;
    }
    throw new PsycoError(errorMessage, 'EXECUTION_ERROR', {
      ...context,
      originalError: error instanceof Error ? error.message : String(error)
    });
  }
}

/**
 * Проверка условия с выбросом ошибки
 */
export function assert(condition: boolean, error: PsycoError): void {
  if (!condition) {
    throw error;
  }
}

/**
 * Проверка существования значения с выбросом ошибки
 */
export function assertExists<T>(value: T | null | undefined, error: PsycoError): T {
  if (value === null || value === undefined) {
    throw error;
  }
  return value;
} 